import React from "react";
import { COLORS } from "../../../theme/colors";
import { MATERIALS, RADIUS } from "../../../theme/materials";
import IconTile from "../../../components/IconTile";
import { resolveTemplateIcon, categoryLabel } from "./iconForTemplate";
import { useHoverTrigger } from "../../library/iphone-grid/TileHoverPreview";

// agent-experience-redesign-v1.md §20.4 / M2.
//
// Marketplace iPhone-grid cell. Distinct from <AgentTile> per the
// §11/§20.5 visual differentiation rule:
//   • IconTile @ 96px (single density — no compact toggle on Marketplace)
//   • Name + author + version chip + install count + ★rating
//   • NO status dot (templates don't have a runtime status — Library
//     tiles' status dot is the visual signature that says "this is a
//     running agent in your tenant")
//   • ALWAYS-VISIBLE Install CTA per §20.12 Q4
//   • Featured templates render an orange outline + "★ Featured" pill
//   • Hover/long-press hits the same useHoverTrigger as Library so the
//     parent renders <TileHoverPreview kind="template"> without two
//     separate trigger paths.

export default function TemplateTile({
  template,
  onClick,
  onInstall,
  onContextMenu,
  onHover,
  onHoverEnd,
  active = false,
  density = "comfortable",
}) {
  const compact = density === "compact";
  const { category, iconName } = resolveTemplateIcon(template);
  const featured = !!template.is_featured;
  const installCount = template.install_count || 0;
  const rating = template.avg_rating;
  const versionLabel = template.schema_version
    ? `v${String(template.schema_version)}`
    : (template.version ? `v${String(template.version).replace(/^v+/, "")}` : null);

  const hoverHandlers = useHoverTrigger({
    onTrigger: (rect) => onHover && onHover(template, rect),
    onCancel: () => onHoverEnd && onHoverEnd(),
  });

  return (
    <div
      data-testid={`marketplace-tile-${template.id || template.slug || ""}`}
      data-template-id={template.id}
      onClick={() => onClick && onClick(template)}
      onContextMenu={(e) => {
        if (onContextMenu) {
          e.preventDefault();
          onContextMenu(template, { x: e.clientX, y: e.clientY });
        }
      }}
      title={template.name}
      {...hoverHandlers}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = `${MATERIALS.spatialShadow()}, 0 0 0 1px ${COLORS.accent}40`;
        hoverHandlers.onMouseEnter && hoverHandlers.onMouseEnter(e);
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = MATERIALS.spatialShadow();
        hoverHandlers.onMouseLeave && hoverHandlers.onMouseLeave(e);
      }}
      style={{
        ...MATERIALS.thickGlass(),
        borderRadius: RADIUS.inner,
        padding: compact ? 10 : 16,
        display: "flex", flexDirection: "column",
        gap: compact ? 6 : 10,
        cursor: "pointer",
        boxShadow: MATERIALS.spatialShadow(),
        border: featured
          ? `1px solid ${COLORS.orange}88`
          : `1px solid ${COLORS.border}`,
        transition: "transform 0.18s, box-shadow 0.18s",
        position: "relative",
      }}
    >
      {/* Featured pill — orange outline doppelgänger */}
      {featured && (
        <div style={{
          position: "absolute", top: 10, right: 10,
          padding: "2px 8px", borderRadius: 999,
          fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
          background: `${COLORS.orange}22`, color: COLORS.orange,
          border: `1px solid ${COLORS.orange}66`,
        }}>★ Featured</div>
      )}

      {/* Header row: IconTile + version chip */}
      <div style={{ display: "flex", alignItems: "center", gap: compact ? 8 : 12 }}>
        <IconTile category={category} iconName={iconName} size={compact ? 40 : 56} active={active} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: COLORS.text, lineHeight: 1.2,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{template.name}</div>
          <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 3 }}>
            {template.author_name || "Unknown"}
            {versionLabel && <span style={{ marginLeft: 6, fontFamily: "monospace" }}>· {versionLabel}</span>}
          </div>
        </div>
      </div>

      {/* Description (2-line clamp) — hidden in compact */}
      {!compact && (
        <div style={{
          fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          overflow: "hidden", minHeight: 36,
        }}>
          {template.description || "No description"}
        </div>
      )}

      {/* Tags — hidden in compact */}
      {!compact && template.tags?.length > 0 && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {template.tags.slice(0, 3).map((tag) => (
            <span key={tag} style={{
              padding: "2px 8px", borderRadius: 4, fontSize: 10,
              background: COLORS.surface, color: COLORS.textDim,
              border: `1px solid ${COLORS.border}`,
            }}>{tag}</span>
          ))}
        </div>
      )}

      {/* Footer: meta + Install CTA */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto", paddingTop: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 11, color: COLORS.textDim }}>
            {installCount} {installCount === 1 ? "install" : "installs"}
          </span>
          {rating != null && (
            <span style={{ fontSize: 11, color: COLORS.orange }}>
              {"★"} {Number(rating).toFixed(1)}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onInstall && onInstall(template); }}
          style={{
            background: COLORS.accent, color: "#fff", border: "none",
            padding: "5px 14px", borderRadius: 6,
            fontSize: 11, fontWeight: 700, cursor: "pointer",
            letterSpacing: "0.02em",
          }}
        >Install</button>
      </div>

      {/* Category chip — bottom-left, subtle. Hidden in compact mode. */}
      {!compact && (
        <div style={{
          fontSize: 9, color: COLORS.textDim,
          textTransform: "uppercase", letterSpacing: "0.06em", marginTop: -4,
        }}>
          {categoryLabel(template.category)}
        </div>
      )}
    </div>
  );
}
